import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RegistroContatoDataService } from '../../pages/negocio/cliente/components/registro-contato/shared/services/RegistroContatoController/registroContato-data.service';
import { FiltroRegistroContatoSignature } from '../../pages/negocio/cliente/components/registro-contato/shared/models/signature/FiltroRegistroContatoSignature';
import { RegistroContatoSimplificadoResult } from '../../pages/negocio/cliente/components/registro-contato/shared/models/result/RegistroContatoSimplificadoResult';

interface Compromisso {
  dtRetorno?: string;
  feedback?: string;
  nomeCliente?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: RegistroContatoSimplificadoResult[];
}

@Injectable({
  providedIn: 'root'
})
export class CompromissosCacheService {
  private readonly CACHE_KEY = 'compromissos-cache';
  private readonly CACHE_TIMESTAMP_KEY = 'compromissos-cache-timestamp';
  private readonly CACHE_DURATION = 15 * 60 * 1000; // 15 minutos em millisegundos

  private compromissosSubject = new BehaviorSubject<Compromisso[]>([]);
  public compromissos$ = this.compromissosSubject.asObservable();

  constructor(private registroContatoService: RegistroContatoDataService) {}

  carregarCompromissos(forcarRecarregamento: boolean = false): Observable<Compromisso[]> {
    if (!forcarRecarregamento && this.isCacheValido()) {
      const compromissosCache = this.obterCompromissosDoCache();
      if (compromissosCache.length > 0) {
        this.compromissosSubject.next(compromissosCache);
        return of(compromissosCache);
      }
    }

    const filtro: FiltroRegistroContatoSignature = {
      dataInicio: null,
      dataFim: null
    };

    return this.registroContatoService.GetTodosRegistrosContatoClienteSimplificados(filtro)
      .pipe(
        map((response: any) => {
          // Verifica se a resposta tem a estrutura esperada da API
            if (response.success && Array.isArray(response.data)) {
              return response.data.filter(item => item.dtRetorno);
            }
          return [];
        }),
        tap(compromissos => {
          this.salvarCompromissosNoCache(compromissos);
          this.compromissosSubject.next(compromissos);
        })
      );
  }

  obterCompromissosHoje(): Compromisso[] {
    const compromissos = this.compromissosSubject.value;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    return compromissos.filter(compromisso => {
      if (!compromisso.dtRetorno) return false;
      const dataCompromisso = new Date(compromisso.dtRetorno);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === hoje.getTime();
    });
  }

  obterCompromissosDoMes(ano: number, mes: number): Compromisso[] {
    const compromissos = this.compromissosSubject.value;
    
    return compromissos.filter(compromisso => {
      if (!compromisso.dtRetorno) return false;
      const dataCompromisso = new Date(compromisso.dtRetorno);
      return dataCompromisso.getFullYear() === ano && dataCompromisso.getMonth() === mes;
    });
  }

  obterCompromissosDoDia(data: Date): Compromisso[] {
    const compromissos = this.compromissosSubject.value;
    const dataComparacao = new Date(data);
    dataComparacao.setHours(0, 0, 0, 0);

    return compromissos.filter(compromisso => {
      if (!compromisso.dtRetorno) return false;
      const dataCompromisso = new Date(compromisso.dtRetorno);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === dataComparacao.getTime();
    });
  }

  limparCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    this.compromissosSubject.next([]);
  }

  private isCacheValido(): boolean {
    const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;

    const cacheTime = parseInt(timestamp, 10);
    const agora = Date.now();
    
    return (agora - cacheTime) < this.CACHE_DURATION;
  }

  private obterCompromissosDoCache(): Compromisso[] {
    try {
      const compromissosJson = localStorage.getItem(this.CACHE_KEY);
      if (!compromissosJson) return [];
      
      const compromissos = JSON.parse(compromissosJson);
      return Array.isArray(compromissos) ? compromissos : [];
    } catch (error) {
      console.error('Erro ao recuperar compromissos do cache:', error);
      return [];
    }
  }

  private salvarCompromissosNoCache(compromissos: Compromisso[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(compromissos));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Erro ao salvar compromissos no cache:', error);
    }
  }

} 